import { useState } from 'react'
import { AgentEditor } from '@/components/agents/AgentEditor.tsx'
import { AgentList } from '@/components/agents/AgentList.tsx'
import { EmptyState } from '@/components/ui/primitives.tsx'
import { useAgents } from '@/hooks/use-agents.ts'
import { useSession } from '@/hooks/use-api.ts'
import { useT } from '@/i18n/index.ts'

/**
 * The agent studio.
 *
 * An agent is stored configuration rather than code, so this screen is the whole change
 * made visible: what the platform will do, and what its definition merged over the platform's
 * defaults actually produces — editable without a deploy. It is the same for everyone who can
 * use the agent (PRD-26 D-23); who that is, is decided with the audience picker, not here.
 *
 * Gated on `admin` because `GET /api/admin/agents/:agentId` is: an operator can list
 * agents and test-resolve them, but cannot read a definition's body, and a studio that renders
 * a rail beside a permission error is worse than one that says so. The components below
 * all take `canEdit`, so relaxing that endpoint to `operator` later is this one predicate.
 *
 * The rail and the editor are siblings in a row on a wide screen and a column on a narrow
 * one. They are never a drawer: an operator reads one agent against the list of agents, and
 * hiding one behind a toggle turns comparing two of them into a memory exercise.
 */
export const AgentsPage = () => {
  // Read in the body, so a locale switch re-renders this screen rather than leaving it in
  // whichever language the bundle was first evaluated in (PRD-27 F-3, LOC-012).
  const t = useT().admin.studio.page
  const session = useSession()
  const roles = session.data?.user?.roles ?? []
  const canView = roles.includes('admin')
  const canEdit = canView

  const agents = useAgents(canView)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (!canView) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-10">
        <EmptyState title={t.adminOnly} body={t.adminOnlyBody} />
      </div>
    )
  }

  const selected = agents.data?.items.find((agent) => agent.agentId === selectedId) ?? agents.data?.items[0] ?? null

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <AgentList
        items={agents.data?.items ?? []}
        selectedId={selected?.agentId ?? null}
        onSelect={setSelectedId}
        loading={agents.isLoading}
        error={agents.error}
        canEdit={canEdit}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {selected ? (
          // Remounting per agent keeps unsaved drafts from leaking across a switch: the
          // editor's state is per-agent and there is no correct way to carry it over.
          <AgentEditor key={selected.agentId} agentId={selected.agentId} canEdit={canEdit} />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-10">
            <EmptyState title={t.noneSelected} body={agents.isLoading ? t.noneSelectedLoading : t.noneSelectedBody} />
          </div>
        )}
      </div>
    </div>
  )
}
